import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { applyTheme } from "@/lib/themes";
import { Package, Heart, ShoppingCart } from "lucide-react";
import ShopNav from "@/components/ShopNav";
import { colorHex } from "@/pages/ShopPage";

const STATUS_COLORS = { pending: "#9ca3af", confirmed: "#1E88E5", packed: "#f59e0b", shipped: "#8b5cf6", delivered: "green", cancelled: "#FF3B30" };

export function ShopOrdersPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "demo";
  const [orders, setOrders] = useState([]);
  const load = useCallback(() => api.get(`/shop/orders?token=${token}`).then((r) => setOrders(r.data)).catch(() => {}), [token]);
  useEffect(() => { load(); api.get(`/employee/me?token=${token}`).then((r) => applyTheme(r.data.theme || "standard")).catch(() => {}); }, [load, token]);

  const cancel = async (oid) => { if (!confirm("Cancel this order?")) return; try { await api.post(`/shop/orders/${oid}/cancel?token=${token}`); toast.success("Order cancelled"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Cannot cancel"); } };

  return (
    <div className="min-h-screen" style={{ background: "var(--bq-bg)", color: "var(--bq-text)" }}>
      <ShopNav token={token} active="orders" />
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6">
        <h1 className="text-2xl font-black uppercase mb-4">My Orders</h1>
        {orders.length === 0 ? (
          <div className="brutal-card p-12 text-center" data-testid="orders-empty"><Package size={48} className="mx-auto mb-3" style={{ color: "var(--bq-muted)" }} /><p className="font-black uppercase">No orders yet</p></div>
        ) : (
          <div className="space-y-3" data-testid="orders-list">
            {orders.map((o) => (
              <div key={o.id} data-testid={`order-${o.order_number}`} className="brutal-card p-4">
                <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
                  <div className="font-black">{o.order_number}</div>
                  <span className="text-xs font-black uppercase px-2 py-1 border-2 border-black" style={{ background: STATUS_COLORS[o.status] || "#ddd", color: ["pending"].includes(o.status) ? "#111" : "#fff" }}>{o.status}</span>
                </div>
                <div className="text-xs mb-2" style={{ color: "var(--bq-muted)" }}>{new Date(o.created_at).toLocaleDateString()} · Est. delivery {o.estimated_delivery}</div>
                <div className="space-y-1">{o.lines.map((l, i) => <div key={i} className="flex justify-between text-sm"><span>{l.name} × {l.qty} {[l.color, l.adult_size, l.kids_size].filter(Boolean).length ? `(${[l.color, l.adult_size, l.kids_size].filter(Boolean).join(", ")})` : ""}</span><span>₹{l.line_total}</span></div>)}</div>
                <div className="border-t-2 border-black mt-2 pt-2 flex flex-wrap justify-between items-center gap-2 text-sm">
                  <div>Paid <b>₹{o.amount_paid}</b>{o.points_used > 0 && <> · Redeemed <b>{o.points_used} pts</b></>} · {o.payment_method?.toUpperCase()}</div>
                  {!["shipped", "delivered", "cancelled"].includes(o.status) && <button data-testid={`cancel-${o.order_number}`} onClick={() => cancel(o.id)} className="text-xs font-black uppercase underline" style={{ color: "var(--bq-danger)" }}>Cancel Order</button>}
                </div>
                <div className="text-xs mt-2" style={{ color: "var(--bq-muted)" }}>Deliver to: {o.address?.recipient_name}, {o.address?.line1}, {o.address?.city} {o.address?.pincode}</div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export function WishlistPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "demo";
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get(`/shop/wishlist?token=${token}`).then((r) => setItems(r.data)).catch(() => {}), [token]);
  useEffect(() => { load(); api.get(`/employee/me?token=${token}`).then((r) => applyTheme(r.data.theme || "standard")).catch(() => {}); }, [load, token]);

  const remove = async (pid) => { await api.delete(`/shop/wishlist/${pid}?token=${token}`); load(); };
  const move = async (p) => { try { await api.post(`/shop/cart?token=${token}`, { product_id: p.id, qty: 1, color: p.colors?.[0], adult_size: p.adult_sizes?.[0], is_kids: p.is_kids }); await api.delete(`/shop/wishlist/${p.id}?token=${token}`); toast.success("Moved to cart"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  return (
    <div className="min-h-screen" style={{ background: "var(--bq-bg)", color: "var(--bq-text)" }}>
      <ShopNav token={token} active="wishlist" />
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6">
        <h1 className="text-2xl font-black uppercase mb-4">Wishlist</h1>
        {items.length === 0 ? (
          <div className="brutal-card p-12 text-center" data-testid="wishlist-empty"><Heart size={48} className="mx-auto mb-3" style={{ color: "var(--bq-muted)" }} /><p className="font-black uppercase">Your wishlist is empty</p><button onClick={() => navigate(`/shop?token=${token}`)} className="brutal-btn mt-4">Browse Shop</button></div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="wishlist-items">
            {items.map((p) => (
              <div key={p.id} data-testid={`wish-item-${p.id}`} className="brutal-card p-3 flex gap-3">
                <img src={p.images?.[0]} alt={p.name} className="w-20 h-20 object-cover border-2 border-black" />
                <div className="flex-1">
                  <div className="font-black leading-tight">{p.name}</div>
                  <div className="font-black">₹{p.price_after_discount}</div>
                  <div className="flex gap-2 mt-2">
                    <button data-testid={`wish-move-${p.id}`} onClick={() => move(p)} className="brutal-btn text-xs"><ShoppingCart size={12} className="inline" /> To Cart</button>
                    <button data-testid={`wish-remove-${p.id}`} onClick={() => remove(p.id)} className="brutal-btn-secondary text-xs">Remove</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
